import React, { useEffect, useRef, useState } from 'react';
import Slider from '../components/Slider';
import Card from '../components/Card';
import AnimatedSection from '../components/AnimatedSection';
import { sliderData, features, stats, testimonials } from '../data/content';
import '../styles/animations.css';

const Home = () => {
  const marqueeRef = useRef(null);
  const backgroundRef = useRef(null);
  const [showShortcutMenu, setShowShortcutMenu] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeShortcut, setActiveShortcut] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Client logos data with images
  const clientLogos = [
    { name: 'KLES', image: 'https://upload.wikimedia.org/wikipedia/commons/6/66/KLE_logo.jpg', width: '120px' },
    { name: 'University of Agricultural Sciences Dharwad', image: 'https://scontent.fblr4-3.fna.fbcdn.net/v/t1.6435-9/117156607_581645145813083_6958087686720714711_n.jpg?_nc_cat=108&ccb=1-7&_nc_sid=1d70fc&_nc_ohc=ZQQh-Bo-POwQ7kNvwHgMBBe&_nc_oc=Adp1g8kmwWoqHwrYYqYoowMHq7w-3uaEuUJXJfxrjeMr_m0KGwugzFr240KKRwwZhtjpA7pjkgAsAg-6VjwHI1wE&_nc_zt=23&_nc_ht=scontent.fblr4-3.fna&_nc_gid=h1PAE5GdakobpXHyl48-jQ&_nc_ss=7a30f&oh=00_AfxppyH7ZS5_hb_PHpr2UC-_kZLxuVH7b5SJZVkxVRnflg&oe=69EC6B8C', width: '100px' },
    { name: 'VIJAYANAGAR COLLEGE HOSPET', image: 'https://www.vijayanagarcollege.org/wp-content/uploads/2024/12/LOGO-NEW-removebg-preview.png', width: '80px' },
    { name: 'ANJUMAN COLLEGE DHARWAD', image: 'https://anjumancollegedwd.edu.in/wp-content/uploads/2020/07/Ajuman-logo-768x633.png', width: '70px' },
    { name: 'J G College of Commerce, Hubli', image: 'https://klesjgcc.edu.in/wp-content/uploads/2025/08/logo-258x300.png', width: '80px' },
    { name: 'Taranath Shikshana Samsthe Raichur', image: 'https://lvdcollege.com/wp-content/uploads/2025/05/WhatsApp-Image-2025-05-17-at-13.03.50-2048x2048.jpeg', width: '80px' },
    { name: 'Dr. Pandit Puttaraj Gavaiji Music College', image: '	https://ppgmusiccollege.edu.in/wp-content/uploads/2020/11/home_clip_image002-1.jpg', width: '90px' },
    { name: 'S A NAREGAL, COLLEGE', image: 'https://sacollegenaregal.edu.in/wp-content/uploads/2020/09/VTECH-CODERS-College-Emblem-edited.jpg', width: '80px' }
  ];

  // Duplicate logos for seamless scrolling
  const scrollingLogos = [...clientLogos, ...clientLogos, ...clientLogos];

  // Shortcut menu items with colors and page identifiers
  const shortcutItems = [
    { name: 'Products', icon: 'fas fa-box', page: 'products', color: '#3b82f6', description: 'Explore our innovative products' },
    { name: 'Training', icon: 'fas fa-chalkboard-user', page: 'training', color: '#8b5cf6', description: 'Upskill with expert training' },
    { name: 'Services', icon: 'fas fa-cogs', page: 'services', color: '#10b981', description: 'Comprehensive IT solutions' },
    { name: 'Enquiry', icon: 'fas fa-envelope', page: 'contact', color: '#f59e0b', description: 'Get instant support', isWhatsApp: true },
    { name: 'Our Team', icon: 'fas fa-users', page: 'team', color: '#ef4444', description: 'Meet our experts' },
    { name: 'About', icon: 'fas fa-info-circle', page: 'about', color: '#06b6d4', description: 'Learn about us' }
  ];

  // WhatsApp number for enquiry
  const whatsappNumber = "919986352227";

  useEffect(() => {
    // Animated background effect
    const handleMouseMove = (e) => {
      if (backgroundRef.current) {
        const { clientX, clientY } = e;
        const x = (clientX / window.innerWidth) * 100;
        const y = (clientY / window.innerHeight) * 100;
        backgroundRef.current.style.background = `
          radial-gradient(circle at ${x}% ${y}%, 
            rgba(59, 130, 246, 0.15) 0%,
            rgba(139, 92, 246, 0.1) 25%,
            rgba(59, 130, 246, 0.05) 50%,
            transparent 80%
          )
        `;
      }
    };

    // Handle scroll for scroll to top button
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    // Close menus when clicking outside
    const handleClickOutside = (e) => {
      if (!e.target.closest('.shortcut-menu') && !e.target.closest('.fab-container') && !e.target.closest('.bottom-nav')) {
        setShowShortcutMenu(false);
        setShowMobileMenu(false);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Navigation function - This will be called when clicking on any shortcut
  const handleNavigation = (page) => {
    // Close all menus
    setShowShortcutMenu(false);
    setShowMobileMenu(false);
    
    // Smooth scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Dispatch navigation event for the main App to handle
    const navigationEvent = new CustomEvent('navigate', { detail: { page } });
    window.dispatchEvent(navigationEvent);
  };

  const handleShortcutClick = (item, index, e) => {
    e.stopPropagation();
    setActiveShortcut(index);
    setTimeout(() => setActiveShortcut(null), 500);
    
    if (item.isWhatsApp) {
      const message = "Hi! I'm interested in VTECH CODERS services. Could you please provide more information?";
      const encodedMessage = encodeURIComponent(message);
      window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');
    } else {
      // Navigate to the selected page
      handleNavigation(item.page);
    }
  };

  const toggleMenu = (e) => {
    e.stopPropagation();
    setShowShortcutMenu(!showShortcutMenu);
    setShowMobileMenu(!showMobileMenu);
  };

  return (
    <div className="home-container" ref={backgroundRef}>
      {/* Animated Background Elements */}
      <div className="animated-bg">
        <div className="bg-gradient-1"></div>
        <div className="bg-gradient-2"></div>
        <div className="bg-gradient-3"></div>
        <div className="grid-lines"></div>
        <div className="particles">
          {[...Array(30)].map((_, i) => (
            <div key={i} className="particle" style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`
            }}></div>
          ))}
        </div>
      </div>

      {/* Quick Access Button - Always Visible */}
      <button className="quick-access-btn" onClick={toggleMenu}>
        <i className="fas fa-bolt"></i>
        <span>Quick Links</span>
      </button>

      {/* Desktop Floating Shortcut Menu - Appears on Click */}
      <div className={`shortcut-menu ${showShortcutMenu ? 'visible' : ''}`}>
        <div className="shortcut-header">
          <i className="fas fa-bolt"></i>
          <span>Quick Navigation</span>
          <button className="close-menu" onClick={toggleMenu}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="shortcut-items">
          {shortcutItems.map((item, idx) => (
            <div 
              key={idx}
              className={`shortcut-item ${activeShortcut === idx ? 'clicked' : ''}`}
              onClick={(e) => handleShortcutClick(item, idx, e)}
              style={{ '--item-color': item.color }}
            >
              <div className="shortcut-icon" style={{ background: `${item.color}20` }}>
                <i className={item.icon} style={{ color: item.color }}></i>
              </div>
              <div className="shortcut-content">
                <span className="shortcut-name">{item.name}</span>
                <span className="shortcut-desc">{item.description}</span>
              </div>
              <div className="shortcut-arrow">
                <i className="fas fa-arrow-right"></i>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Bottom Sheet Menu */}
      <div className={`mobile-menu-overlay ${showMobileMenu ? 'open' : ''}`} onClick={() => setShowMobileMenu(false)}></div>
      <div className={`mobile-menu-bottom ${showMobileMenu ? 'open' : ''}`}>
        <div className="mobile-menu-header">
          <i className="fas fa-bolt"></i>
          <span>Quick Links</span>
          <button className="close-menu" onClick={() => setShowMobileMenu(false)}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="mobile-menu-items">
          {shortcutItems.map((item, idx) => (
            <div 
              key={idx}
              className="mobile-menu-item"
              onClick={(e) => handleShortcutClick(item, idx, e)}
              style={{ borderLeftColor: item.color }}
            >
              <div className="mobile-item-icon" style={{ background: `${item.color}20` }}>
                <i className={item.icon} style={{ color: item.color }}></i>
              </div>
              <div className="mobile-item-content">
                <span className="mobile-item-name">{item.name}</span>
                <span className="mobile-item-desc">{item.description}</span>
              </div>
              <i className="fas fa-chevron-right"></i>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll to Top Button */}
      <button className={`scroll-top-btn ${showScrollTop ? 'visible' : ''}`} onClick={scrollToTop}>
        <i className="fas fa-arrow-up"></i>
        <span>Top</span>
      </button>

      {/* Quick Action Floating Buttons */}
      <div className="quick-actions">
        <a 
          href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Hi! I'm interested in your services. Please help me.")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="quick-action whatsapp-action"
        >
          <i className="fab fa-whatsapp"></i>
          <span>WhatsApp</span>
        </a>
        <a 
          href="tel:+919986352227"
          className="quick-action call-action"
        >
          <i className="fas fa-phone-alt"></i>
          <span>Call Now</span>
        </a>
      </div>

      <Slider slides={sliderData} />
      
      {/* Welcome Banner */}
      <div className="welcome-banner">
        <div className="container">
          <div className="welcome-content">
            <i className="fas fa-gem"></i>
            <p>Welcome to VTECH CODERS - Your Trusted Technology Partner Since 2011</p>
          </div>
        </div>
      </div>
      
      <section style={{ position: 'relative', zIndex: 2 }} id="features">
        <div className="container">
          <AnimatedSection animation="fade-up">
            <h2 className="section-title">Why Choose VTECH CODERS?</h2>
          </AnimatedSection>
          <div className="cards-grid">
            {features.map((feature, idx) => (
              <AnimatedSection key={idx} animation="scale-up" delay={idx * 100}>
                <Card {...feature} />
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section style={{ background: 'linear-gradient(135deg, rgba(248,250,252,0.9) 0%, rgba(238,242,255,0.9) 100%)', position: 'relative', zIndex: 2 }} id="impact">
        <div className="container">
          <AnimatedSection animation="fade-up">
            <h2 className="section-title">Our Impact by Numbers</h2>
          </AnimatedSection>
          <div className="cards-grid">
            {stats.map((stat, idx) => (
              <AnimatedSection key={idx} animation="scale-up" delay={idx * 100}>
                <Card {...stat} isStat={true} />
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section style={{ position: 'relative', zIndex: 2 }} id="testimonials">
        <div className="container">
          <AnimatedSection animation="fade-up">
            <h2 className="section-title">What Our Clients Say</h2>
          </AnimatedSection>
          <div className="cards-grid">
            {testimonials.map((testimonial, idx) => (
              <AnimatedSection key={idx} animation="fade-up" delay={idx * 100}>
                <div className="card" style={{ textAlign: 'center' }}>
                  <i className="fas fa-quote-left" style={{ fontSize: '2rem', color: '#3b82f6', opacity: 0.5 }}></i>
                  <p style={{ margin: '1rem 0', lineHeight: 1.6 }}>"{testimonial.text}"</p>
                  <h4>{testimonial.name}</h4>
                  <small style={{ color: '#3b82f6' }}>{testimonial.role}</small>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted By Clients Section */}
      <section style={{ 
        background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)', 
        position: 'relative', 
        zIndex: 2,
        overflow: 'hidden',
        padding: '4rem 0'
      }} id="clients">
        <div className="container">
          <AnimatedSection animation="fade-up">
            <h2 className="section-title">Trusted By Industry Leaders</h2>
          </AnimatedSection>
          
          <div className="logo-marquee">
            <div className="marquee-track" ref={marqueeRef}>
              {scrollingLogos.map((logo, idx) => (
                <div key={idx} className="marquee-item">
                  <img 
                    src={logo.image} 
                    alt={logo.name}
                    style={{
                      width: logo.width,
                      height: 'auto',
                      maxHeight: '60px',
                      objectFit: 'contain',
                      filter: 'grayscale(0.2)',
                      transition: 'all 0.3s ease'
                    }}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/120x60?text=' + logo.name;
                    }}
                  />
                  <p style={{ 
                    fontSize: '0.8rem', 
                    marginTop: '0.5rem',
                    color: '#64748b',
                    fontWeight: 500
                  }}>
                    {logo.name}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="client-grid" style={{ marginTop: '3rem' }}>
            <AnimatedSection animation="fade-up">
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '2rem',
                alignItems: 'center',
                justifyItems: 'center'
              }}>
                {clientLogos.slice(0, 4).map((logo, idx) => (
                  <div key={idx} className="client-card">
                    <img 
                      src={logo.image} 
                      alt={logo.name}
                      style={{
                        width: logo.width,
                        height: 'auto',
                        maxHeight: '50px',
                        objectFit: 'contain',
                        marginBottom: '0.5rem'
                      }}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/100x50?text=' + logo.name;
                      }}
                    />
                    <p style={{ 
                      fontSize: '0.85rem', 
                      color: '#64748b',
                      fontWeight: 500
                    }}>
                      {logo.name}
                    </p>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <style jsx>{`
        /* Animated Background Styles */
        .home-container {
          position: relative;
          min-height: 100vh;
        }
        
        .animated-bg {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 0;
          overflow: hidden;
        }
        
        .bg-gradient-1,
        .bg-gradient-2,
        .bg-gradient-3 {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          opacity: 0.5;
          animation: float 20s ease-in-out infinite;
        }
        
        .bg-gradient-1 {
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(59,130,246,0.4), rgba(139,92,246,0.2));
          top: -250px;
          left: -250px;
          animation-delay: 0s;
        }
        
        .bg-gradient-2 {
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(139,92,246,0.3), rgba(59,130,246,0.1));
          bottom: -300px;
          right: -300px;
          animation-delay: -5s;
        }
        
        .bg-gradient-3 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(236,72,153,0.2), rgba(59,130,246,0.1));
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation-delay: -10s;
        }
        
        .grid-lines {
          position: absolute;
          width: 100%;
          height: 100%;
          background-image: 
            linear-gradient(rgba(59,130,246,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59,130,246,0.05) 1px, transparent 1px);
          background-size: 50px 50px;
          animation: gridMove 20s linear infinite;
        }
        
        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
        
        .particles {
          position: absolute;
          width: 100%;
          height: 100%;
        }
        
        .particle {
          position: absolute;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          border-radius: 50%;
          opacity: 0.3;
          animation: floatParticle linear infinite;
        }
        
        @keyframes floatParticle {
          0% {
            transform: translateY(100vh) rotate(0deg);
            opacity: 0;
          }
          10% { opacity: 0.5; }
          90% { opacity: 0.5; }
          100% {
            transform: translateY(-100vh) rotate(360deg);
            opacity: 0;
          }
        }
        
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(50px, -50px) scale(1.1); }
          66% { transform: translate(-50px, 50px) scale(0.9); }
        }
        
        /* Quick Access Button */
        .quick-access-btn {
          position: fixed;
          bottom: 30px;
          left: 30px;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 50px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 600;
          font-size: 1rem;
          z-index: 1000;
          box-shadow: 0 4px 20px rgba(59,130,246,0.4);
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }
        
        .quick-access-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 6px 25px rgba(59,130,246,0.6);
        }
        
        /* Desktop Floating Shortcut Menu */
        .shortcut-menu {
          position: fixed;
          bottom: 100px;
          left: 30px;
          width: 320px;
          background: rgba(255,255,255,0.98);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.2);
          border: 1px solid rgba(59,130,246,0.2);
          z-index: 999;
          opacity: 0;
          visibility: hidden;
          transform: translateY(20px) scale(0.95);
          transition: all 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1);
        }
        
        .shortcut-menu.visible {
          opacity: 1;
          visibility: visible;
          transform: translateY(0) scale(1);
        }
        
        .shortcut-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 16px 20px;
          border-bottom: 1px solid rgba(59,130,246,0.1);
          font-weight: 600;
          color: #1e293b;
        }
        
        .shortcut-header i {
          color: #3b82f6;
          font-size: 1.2rem;
        }
        
        .close-menu {
          margin-left: auto;
          background: none;
          border: none;
          cursor: pointer;
          color: #64748b;
          font-size: 1rem;
          padding: 5px;
          border-radius: 50%;
          transition: all 0.3s ease;
        }
        
        .close-menu:hover {
          background: rgba(0,0,0,0.05);
          color: #ef4444;
        }
        
        .shortcut-items {
          padding: 8px;
          max-height: 400px;
          overflow-y: auto;
        }
        
        .shortcut-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .shortcut-item:hover {
          background: linear-gradient(135deg, rgba(59,130,246,0.08), rgba(139,92,246,0.08));
          transform: translateX(5px);
        }
        
        .shortcut-item.clicked {
          animation: clickRipple 0.5s ease;
        }
        
        @keyframes clickRipple {
          0% { transform: scale(0.95); opacity: 0.8; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        .shortcut-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .shortcut-icon i {
          font-size: 1.3rem;
        }
        
        .shortcut-content {
          flex: 1;
        }
        
        .shortcut-name {
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
          display: block;
        }
        
        .shortcut-desc {
          font-size: 0.7rem;
          color: #64748b;
          display: block;
        }
        
        .shortcut-arrow {
          opacity: 0;
          transform: translateX(-5px);
          transition: all 0.3s ease;
        }
        
        .shortcut-item:hover .shortcut-arrow {
          opacity: 1;
          transform: translateX(0);
        }
        
        .shortcut-arrow i {
          color: #3b82f6;
        }
        
        /* Mobile Bottom Sheet Menu */
        .mobile-menu-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 999;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
        }
        
        .mobile-menu-overlay.open {
          opacity: 1;
          visibility: visible;
        }
        
        .mobile-menu-bottom {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: rgba(255,255,255,0.98);
          backdrop-filter: blur(20px);
          border-radius: 24px 24px 0 0;
          z-index: 1000;
          transform: translateY(100%);
          transition: transform 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1);
          max-height: 80vh;
          overflow-y: auto;
        }
        
        .mobile-menu-bottom.open {
          transform: translateY(0);
        }
        
        .mobile-menu-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 20px;
          border-bottom: 1px solid rgba(59,130,246,0.1);
          font-weight: 600;
          font-size: 1.1rem;
          color: #1e293b;
        }
        
        .mobile-menu-header i {
          color: #3b82f6;
          font-size: 1.2rem;
        }
        
        .mobile-menu-items {
          padding: 12px;
        }
        
        .mobile-menu-item {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px;
          border-radius: 14px;
          cursor: pointer;
          transition: all 0.3s ease;
          border-left: 3px solid transparent;
        }
        
        .mobile-menu-item:active {
          background: rgba(59,130,246,0.1);
        }
        
        .mobile-item-icon {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .mobile-item-icon i {
          font-size: 1.3rem;
        }
        
        .mobile-item-content {
          flex: 1;
        }
        
        .mobile-item-name {
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
          display: block;
        }
        
        .mobile-item-desc {
          font-size: 0.7rem;
          color: #64748b;
          display: block;
        }
        
        .mobile-menu-item i:last-child {
          color: #94a3b8;
          font-size: 0.8rem;
        }
        
        /* Welcome Banner */
        .welcome-banner {
          background: linear-gradient(135deg, rgba(59,130,246,0.1), rgba(139,92,246,0.1));
          padding: 0.8rem 0;
          position: relative;
          z-index: 2;
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(59,130,246,0.2);
        }
        
        .welcome-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          animation: slideInLeft 0.5s ease;
        }
        
        .welcome-content i {
          font-size: 1.2rem;
          color: #3b82f6;
          animation: pulse 2s ease-in-out infinite;
        }
        
        .welcome-content p {
          color: #1e293b;
          font-weight: 500;
          margin: 0;
        }
        
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        
        /* Scroll to Top Button */
        .scroll-top-btn {
          position: fixed;
          bottom: 30px;
          right: 30px;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          color: white;
          border: none;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2px;
          z-index: 998;
          opacity: 0;
          visibility: hidden;
          transform: scale(0);
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(59,130,246,0.3);
        }
        
        .scroll-top-btn.visible {
          opacity: 1;
          visibility: visible;
          transform: scale(1);
        }
        
        .scroll-top-btn:hover {
          transform: scale(1.1);
        }
        
        /* Quick Action Buttons */
        .quick-actions {
          position: fixed;
          bottom: 30px;
          right: 100px;
          display: flex;
          gap: 12px;
          z-index: 998;
        }
        
        .quick-action {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border-radius: 50px;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.85rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        }
        
        .whatsapp-action {
          background: linear-gradient(135deg, #25D366, #128C7E);
          color: white;
        }
        
        .whatsapp-action:hover {
          transform: translateY(-2px);
        }
        
        .call-action {
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: white;
        }
        
        .call-action:hover {
          transform: translateY(-2px);
        }
        
        /* Marquee Styles */
        .logo-marquee {
          width: 100%;
          overflow: hidden;
          background: transparent;
          padding: 2rem 0;
          position: relative;
        }
        
        .logo-marquee::before,
        .logo-marquee::after {
          content: '';
          position: absolute;
          top: 0;
          width: 100px;
          height: 100%;
          z-index: 2;
          pointer-events: none;
        }
        
        .logo-marquee::before {
          left: 0;
          background: linear-gradient(to right, rgba(248,250,252,1), transparent);
        }
        
        .logo-marquee::after {
          right: 0;
          background: linear-gradient(to left, rgba(248,250,252,1), transparent);
        }
        
        .marquee-track {
          display: flex;
          animation: scroll 30s linear infinite;
          width: max-content;
        }
        
        .marquee-track:hover {
          animation-play-state: paused;
        }
        
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        
        .marquee-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          margin: 0 2rem;
          padding: 1rem;
          transition: all 0.3s ease;
          cursor: pointer;
          min-width: 150px;
        }
        
        .marquee-item:hover {
          transform: scale(1.1);
        }
        
        .client-card {
          text-align: center;
          padding: 1rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.05);
          transition: all 0.3s ease;
          cursor: pointer;
          width: 100%;
        }
        
        .client-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(59,130,246,0.15);
        }
        
        /* Responsive Styles */
        @media (max-width: 768px) {
          .quick-access-btn {
            bottom: 20px;
            left: 20px;
            padding: 10px 18px;
            font-size: 0.85rem;
          }
          
          .quick-access-btn span {
            display: inline;
          }
          
          .shortcut-menu {
            display: none;
          }
          
          .quick-actions {
            bottom: 20px;
            right: 20px;
            flex-direction: column;
          }
          
          .scroll-top-btn {
            bottom: 20px;
            right: 20px;
            width: 45px;
            height: 45px;
          }
          
          .marquee-item {
            margin: 0 1rem;
            min-width: 100px;
          }
          
          .marquee-item img {
            width: 80px !important;
          }
          
          .welcome-content p {
            font-size: 0.8rem;
          }
        }
        
        @media (min-width: 769px) {
          .mobile-menu-overlay,
          .mobile-menu-bottom {
            display: none;
          }
        }
        
        /* Dark Mode Support */
        @media (prefers-color-scheme: dark) {
          .client-card {
            background: rgba(30, 41, 59, 0.8);
          }
          
          .logo-marquee::before {
            background: linear-gradient(to right, rgba(15,23,42,1), transparent);
          }
          
          .logo-marquee::after {
            background: linear-gradient(to left, rgba(15,23,42,1), transparent);
          }
          
          .shortcut-menu,
          .mobile-menu-bottom {
            background: rgba(30, 41, 59, 0.98);
          }
          
          .shortcut-header,
          .mobile-menu-header {
            color: #f1f5f9;
            border-bottom-color: rgba(59,130,246,0.2);
          }
          
          .shortcut-name,
          .mobile-item-name {
            color: #f1f5f9;
          }
          
          .shortcut-desc,
          .mobile-item-desc {
            color: #94a3b8;
          }
          
          .welcome-content p {
            color: #cbd5e1;
          }
        }
        
        .section-title {
          position: relative;
          display: inline-block;
        }
        
        .section-title::after {
          content: '';
          position: absolute;
          bottom: -10px;
          left: 50%;
          transform: translateX(-50%);
          width: 60px;
          height: 3px;
          background: linear-gradient(95deg, #3b82f6, #8b5cf6);
          border-radius: 3px;
          animation: expandWidth 0.5s ease;
        }
        
        @keyframes expandWidth {
          from { width: 0; }
          to { width: 60px; }
        }
      `}</style>
    </div>
  );
};

export default Home;